import React, { Fragment, useRef, useState, useEffect, useMemo, useCallback } from 'react'
import { Field, Form, Formik } from 'formik'
import TextField from 'components/Common/TextField'
import { Typography, Box } from '@mui/material'
import Button from 'components/Common/Button'
import { AccountFormField, AccountFormLayout } from 'components/Common/AccountFormLayout'
import PhoneField from 'components/Common/PhoneField'
import { store } from 'reduxStore'
import AlertSuccess from './AlertSuccess'
import * as yup from 'yup'
import api from 'api'
import ZendeskLink from 'components/Common/ZendeskLink'
import UserContent from './UserContent'
import BusinessFormControls from './BusinessFormControls'
import { useOnClickOutside } from 'hooks'
import { DISABLED_BUTTON, PRIMARY_BUTTON } from 'themes/theme'

const sx = {
  blockHeader: {
    marginBottom: theme => theme.spacing(2),
    marginTop: theme => theme.spacing(3),
    fontWeight: 'bold',
    fontSize: 19,
    color: '#0076c5',
  },
  textInputRoot: {
    marginTop: 30,
  },
  switcherCustom: {
    opacity: '0.4',
  },
  alert: {
    position: 'relative',
    right: 0,
    marginTop: 24,
    [theme => theme.breakpoints.down('xs')]: {
      right: '0',
    },
  },
  industryNameInputBox: {
    width: '100%',
  },
  industryInputRoot: {
    boxShadow: 'none',
    border: 'none',
  },
  industryInput: {
    border: '1px solid #0000003b',
    borderRadius: 6,
    height: 47,
    boxSizing: 'border-box',
    '&:hover': {
      border: '1px solid #25245F',
    },
    '&:focus': {
      border: '2px solid #06489c',
      borderRadius: 6,
    },
  },
  layoutWrapper: {
    justifyContent: 'left',
  },
  layoutColumn: {
    marginRight: 20,
  },
  hidden: {
    visibility: 'hidden',
    height: 0,
  },
  infoIcon: {
    position: 'absolute',
    top: '-2px',
    left: '80px',
  },
  gridItem: {
    padding: '0 30px',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: '1px',
      height: '100%',
    },
    '&:last-child': {
      paddingRight: 0,
      '&::before': {
        content: 'none',
      },
    },
  },
  contact: {
    marginBottom: theme => theme.spacing(2),
    fontWeight: 'bold',
    fontSize: 18,
    color: '#0076c5',
    fontFamily: 'popins',
  },
}

const schema = yup.object({
  keyPeople: yup
    .array()
    .of(
      yup.object({
        firstName: yup.string().when(['title', 'phone', 'email'], {
          is: (title, phone, email) => [title, phone, email].some(val => Boolean(val)),
          then: schema => schema.required('Name is Required')
        }),
        title: yup.string().notRequired(),
        email: yup.string().email('Enter valid email'),
        phone: yup.string().notRequired()
      })
    )
    .required(),
  businessId: yup.string().required()
})

const makeKeyPeople = people => ({
  firstName: people?.firstName || '',
  title: people?.title || '',
  email: people?.email || '',
  phone: people?.phone || '',
})

const KeyPeople = ({ model, setBusiness }) => {
  const [companyTitles, setCompanyTitles] = useState([])
  const formik = useRef(null)
  const [submitted, setSubmitted] = useState(false)
  const user = store.getState().userReducer.user
  const isFreeUser = user?.accountType === 'free'

  async function getTitle() {
    const titles = await api.getCompanyTitles('')
    setCompanyTitles(titles)
  }

  useEffect(() => {
    getTitle()
  }, [])

  const initialValues = useMemo(() => {
    const keyPeoples = model === null || model.details.keyPeople === null ? [] : model.details.keyPeople
    const count = keyPeoples.length > 5 ? keyPeoples.length : 5
    const result = []
    for (let i = 0; i < count; i++) {
      result.push(makeKeyPeople(keyPeoples[i]))
    }
    return {
      keyPeople: result,
      businessId: model ? model.details.businessId : '',
    }
  }, [model])

  const onFormSubmit = useCallback(
    async (values, formikHelpers) => {
      if (model) {
        formikHelpers.setSubmitting(true)

        const keyPeople = values.keyPeople.filter(people => !!people.firstName)
        const body = {
          keyPeople: keyPeople.length ? keyPeople : null,
          businessId: model.details.businessId,
        }

        try {
          await api.updateMyBusinessKeyPeople(body)
          const updatedBusiness = await api.getUsersBusinessByExtId(model?.ext_id)
          setBusiness(updatedBusiness)
          setSubmitted(true)
        } catch (error) {
          console.error(error)
        } finally {
          formikHelpers.setSubmitting(false)
        }
      }
    },
    [setBusiness, model],
  )

  if (user?.kybValidated === undefined) {
    return (
      <UserContent
        label={user.accountType === 'free' ? 'Subscribe' : 'Find Now'}
        content={
          user.accountType === 'free'
            ? 'Become a premium member to claim your business, add additional fields on your profile and monitor businesses you are interested in.'
            : 'Find your business to claim it, add additional fields and enhance your profile for suppliers, customers and partners.'
        }
      />
    )
  }

  return (
    <>
      <Formik
        innerRef={formik}
        validationSchema={schema}
        initialValues={initialValues}
        onSubmit={onFormSubmit}
        enableReinitialize
      >
        {({ dirty, isSubmitting, setFieldValue, errors, isValid }) => (
          <Form style={{ flex: 1 }}>
            <BusinessFormControls user={user} model={model} dirty={dirty} isSubmitting={isSubmitting} />
            <AccountFormLayout
              leftSide={
                <Fragment>
                  <KeyPeopleItem
                    disabled={isFreeUser}
                    contactIndex={0}
                    set={setFieldValue}
                    companyTitles={companyTitles}
                  />
                  <KeyPeopleItem
                    disabled={isFreeUser}
                    contactIndex={3}
                    set={setFieldValue}
                    companyTitles={companyTitles}
                  />
                </Fragment>
              }
              center={
                <Fragment>
                  <KeyPeopleItem
                    disabled={isFreeUser}
                    contactIndex={1}
                    set={setFieldValue}
                    companyTitles={companyTitles}
                  />
                  <KeyPeopleItem
                    disabled={isFreeUser}
                    contactIndex={4}
                    set={setFieldValue}
                    companyTitles={companyTitles}
                  />
                </Fragment>
              }
              rightSideFullSize={true}
              rightSide={
                <Fragment>
                  <KeyPeopleItem
                    disabled={isFreeUser}
                    contactIndex={2}
                    set={setFieldValue}
                    companyTitles={companyTitles}
                  />
                  <Box display="flex" justifyContent="center">
                    <Button
                      submit
                      loading={isSubmitting}
                      type={!dirty ? DISABLED_BUTTON : PRIMARY_BUTTON}
                      sx={{
                        width: '50%',
                      }}
                    >
                      Update
                    </Button>
                  </Box>
                  <AlertSuccess submitted={submitted} />
                  {errors?.serverError && (
                    <Typography style={{ color: 'red', fontSize: 12, marginTop: 10 }} variant="body1">
                      {errors.serverError}
                    </Typography>
                  )}
                </Fragment>
              }
            />
          </Form>
        )}
      </Formik>
      <div>
        <ZendeskLink />
      </div>
    </>
  )
}

const sxBusinessTitles = {
  categories: {
    position: 'absolute',
    top: 80,
    width: '100%',
    padding: 10,
    zIndex: 1000,
    backgroundColor: '#ffffff',
    overflow: 'hidden',
    overflowY: 'auto',
    maxHeight: 220,
    fontSize: '14px',
    marginTop: '10px',
    boxShadow: '4px 4px 15px rgba(10, 120, 205, 0.08)',
    borderRadius: '5px',
  },
  span: {
    padding: 8,
    cursor: 'pointer',
    display: 'block',
    color: '#66668F',
    borderRadius: '5px',
    '&:hover': {
      backgroundColor: '#F4F4F7',
    },
  },
  closed: {
    display: 'none',
  },
}

const BusinessTitle = ({ categories, setValue, dropToDefault, style, lastSelectedValue, setLastValue }) => {
  const ref = useRef(null)
  useOnClickOutside(ref, () => {
    dropToDefault([])
    setValue(lastSelectedValue)
  })

  const action = item => {
    dropToDefault([])
    setLastValue && setLastValue(item)
    setValue(item)
  }

  if (categories.length === 0) return null

  return (
    <Box ref={ref} sx={sxBusinessTitles.categories} style={style}>
      {categories.map(item => (
        <Box component={"span"} onClick={() => action(item)} sx={sxBusinessTitles.span} key={item}>
          {item}
        </Box>
      ))}
    </Box>
  )
}

const KeyPeopleItem = ({ contactIndex, set, disabled = false }) => {
  const [businessTitles, setBusinessTitles] = useState([])
  const [lastTitle, setLastTitle] = useState('')

  const getCompanyTitles = async search => {
    const { titles } = await api.getCompanyTitles(search)
    setBusinessTitles(titles)
  }

  return (
    <Box sx={sx.wrapper}>
      <Box className="customAuthInput">
        <Typography sx={{ fontWeight: 'bold', marginBottom: '20px', fontSize: '16px', color: '#0076c5' }}>
          Contact {contactIndex + 1}
        </Typography>
        <AccountFormField>
          <Field name={`keyPeople[${contactIndex}].firstName`}>
            {({ field, meta }) => (
              <TextField
                {...field}
                label="Full Name"
                placeholder="Name"
                fullWidth
                borderLess
                // required
                error={Boolean(meta.touched && meta.error)}
                helperText={meta.touched && meta.error}
                disabled={disabled}
              />
            )}
          </Field>
        </AccountFormField>
        <AccountFormField>
          <Field name={`keyPeople[${contactIndex}].title`}>
            {({ field, meta }) => (
              <TextField
                {...field}
                label="Position Title"
                placeholder="Title"
                fullWidth
                borderLess
                error={Boolean(meta.touched && meta.error)}
                helperText={meta.touched && meta.error}
                disabled={disabled}
                isSelect
                onClickSelect={() => {
                  getCompanyTitles('')
                }}
                onChange={e => {
                  if (!e) {
                    getCompanyTitles('')
                    set(`keyPeople[${contactIndex}].title`, '')
                  }
                  getCompanyTitles(e.target.value)
                  set(`keyPeople[${contactIndex}].title`, e.target.value)
                }}
                onFocus={() => {
                  getCompanyTitles('')
                  set(`keyPeople[${contactIndex}].title`, '')
                }}
              />
            )}
          </Field>
          <BusinessTitle
            setValue={value => set(`keyPeople[${contactIndex}].title`, value)}
            dropToDefault={setBusinessTitles}
            categories={businessTitles}
            lastSelectedValue={lastTitle}
            setLastValue={setLastTitle}
          />
        </AccountFormField>
        <AccountFormField>
          <Field name={`keyPeople[${contactIndex}].email`}>
            {({ field, meta }) => (
              <TextField
                {...field}
                label="Email"
                placeholder="example@example.com"
                fullWidth
                borderLess
                error={Boolean(meta.touched && meta.error)}
                helperText={meta.touched && meta.error}
                disabled={disabled}
              />
            )}
          </Field>
        </AccountFormField>
        <AccountFormField>
          <Field name={`keyPeople[${contactIndex}].phone`}>
            {({ field, meta }) => (
              <PhoneField
                {...field}
                fullWidth
                borderLess
                placeholder="ex (XXX) XXX-XXXX"
                label="Phone Number"
                error={Boolean(meta.touched && meta.error)}
                helperText={meta.touched && meta.error}
                disabled={disabled}
              ></PhoneField>
            )}
          </Field>
        </AccountFormField>
      </Box>
    </Box>
  )
}

export default KeyPeople
